import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import useNotification from "../hooks/useNotification";
import { setToastInstance } from "../lib/utils/toast";
import PropTypes from "prop-types";


const NotificationContext = createContext();

export const useToast = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useToast must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { NotificationComponent, triggerNotification } =
    useNotification("top-center");

  const toast = useCallback(
    (message, options = {}) => {
      const { type = "info", duration = 3000, ...otherOptions } = options;

      triggerNotification({
        type,
        message,
        duration,
        ...otherOptions,
      });
    },
    [triggerNotification]
  );

  // Create methods that match react-hot-toast API
  const toastAPI = useMemo(
    () => ({
      success: (message, options = {}) =>
        toast(message, { ...options, type: "success" }),
      error: (message, options = {}) =>
        toast(message, { ...options, type: "error" }),
      warning: (message, options = {}) =>
        toast(message, { ...options, type: "warning" }),
      info: (message, options = {}) =>
        toast(message, { ...options, type: "info" }),
      // Generic toast method
      default: (message, options = {}) =>
        toast(message, { ...options, type: "info" }),
    }),
    [toast]
  );

  // Set the toast instance for the wrapper
  useEffect(() => {
    setToastInstance(toastAPI);
  }, [toastAPI]);

  return (
    <NotificationContext.Provider value={toastAPI}>
      {children}
      {NotificationComponent}
    </NotificationContext.Provider>
  );
};
NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};